import React, { useEffect, useState } from 'react';
import { useAppDispatch, useAppSelector } from 'hooks';

import { useSearch } from '~libs/searchParams';

import { Button, ClickableIcon, ClickableUploadIcon, ErrorResponse, Toolbar } from '~components';
import { SearchBar } from '~components/form-fields/search';
import Icon, { faFileImport, faMagnifyingGlass, faPlus } from '~components/icon';
import { TableView } from '~components/table-view';

import { SahBudget } from '~support-at-home/models';

import columnsFactory from '~support-at-home/forms/sah-budgets/listAll';
import searchFields from '~support-at-home/forms/sah-budgets/search';

import { getAll, uploadFile } from '~support-at-home/actions/sahBudgets';

import { clear, clearError } from '~support-at-home/reducers/sahBudgets';

import {
  selectErrorResponse,
  selectLoading,
  selectSahBudgets,
  selectUploadError,
} from '~support-at-home/selectors/sahBudgets';

import AcoImportModal from './AcoImportModal';
import AddBudgetModal from './AddBudgetModal';
import SahBudgetsModal from './BudgetsModal';

const Budgets: React.FC = () => {
  const dispatch = useAppDispatch();
  const budgets = useAppSelector(selectSahBudgets);
  const loading = useAppSelector(selectLoading) === 'pending';
  const errorResponse = useAppSelector(selectErrorResponse);
  const uploadError = useAppSelector(selectUploadError);

  const [isClientOpen, setClientOpen] = useState<boolean>(false);
  const [selectedClientId, setSelectedClientId] = useState<number | null>(null);
  const [isAddModalOpen, setAddModalOpen] = useState(false);
  const [isAcoImportOpen, setAcoImportOpen] = useState(false);

  const [data, setData] = useState<SahBudget[]>(budgets as SahBudget[]);
  const [searchBarOpen, setSearchBarOpen] = useState<boolean>(false);
  const [searchParams, setSearchParams] = useSearch('sah-client-budgets-search', {});
  const search = (values: { [key: string]: any }) => setSearchParams(values);

  const toggleModal = (toggle: boolean, clientId: number | null = null) => {
    setSelectedClientId(clientId);
    setClientOpen(toggle);
    load();
  };

  const onModalClose = () => {
    toggleModal(false);
    load();
  };

  const openAddModal = () => setAddModalOpen(true);
  const closeAddModal = () => setAddModalOpen(false);
  const openAcoImportModal = () => setAcoImportOpen(true);
  const closeAcoImportModal = () => {
    setAcoImportOpen(false);
    load();
  };

  const load = async () => {
    dispatch(clearError([]));
    dispatch(getAll({ ...searchParams }));
  };

  const onUpload = async (file: File, setProgress: (value: number) => void) => {
    try {
      await dispatch(uploadFile({ file, setProgress })).unwrap();
      await load();
    } catch (error) {
      console.error('Upload failed:', error);
    }
  };

  const columns = columnsFactory((id) => toggleModal(true, id));

  useEffect(() => {
    load();
    return () => {
      dispatch(clear([]));
    };
  }, []);

  useEffect(() => {
    load();
  }, [searchParams]);

  useEffect(() => {
    setData(budgets as SahBudget[]);
  }, [budgets]);

  return (
    <div className="p-2">
      <ErrorResponse message={errorResponse} dataCy="sah-client-budgets-error" />
      <Toolbar
        id="config-budgets"
        rightCluster={
          <>
            <ClickableUploadIcon
              id="sah-client-budgets-file-upload-button"
              icon={faFileImport}
              toolTip="Import CSV / JSON file"
              onUpload={onUpload}
              externalError={uploadError}
            />
            <ClickableIcon
              id="sah-client-budgets-search-button"
              icon={faMagnifyingGlass}
              onClick={() => setSearchBarOpen(!searchBarOpen)}
              toolTip="Toggle Search Bar"
            />
            <Button id="sah-client-budgets-aco-upload-button" size="sm" color="success" onClick={openAcoImportModal}>
              <Icon className="me-2" icon={faFileImport} />
              Import from ACO
            </Button>
            <Button id="sah-client-budgets-add-button" size="sm" color="success" onClick={openAddModal}>
              <Icon className="me-2" icon={faPlus} />
              Add Budget
            </Button>
          </>
        }
      />
      <SearchBar isOpen={searchBarOpen} fields={searchFields} onSubmit={search} />
      <TableView
        id="sah-client-budgets-table"
        columns={columns}
        data={data}
        emptyMessage={'No client SAH Budgets found'}
        isLoading={loading}
        readonly
      />
      <SahBudgetsModal id={selectedClientId} isOpen={isClientOpen} onClose={onModalClose} />
      <AddBudgetModal isOpen={isAddModalOpen} onClose={closeAddModal} onSuccess={load} />
      <AcoImportModal isOpen={isAcoImportOpen} onClose={closeAcoImportModal} />
    </div>
  );
};

export default Budgets;
